"use client";

import React, { ReactNode, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { DrawerComponent } from "modules/layout/DrawerComponent";
import Footer from "modules/common/components/Footer";
import DynamicCardDetailsDialog from "modules/common/components/CardDetailsDialog/dynamicImports/DynamicCardDetailsDialog";
import DynamicQRCodeModal from "modules/common/components/qrCode/DynamicQRCodeModal";
import DynamicConfirmDeleteModal from "modules/common/components/DynamicConfirmDeleteModal";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { useNotificationToasts } from "modules/notifications/hooks/useNotificationToasts";
import DynamicAddCardModal from "modules/cards/actions/components/DynamicAddCardModal";
import DynamicWhatsNewModal from "modules/changelog/components/DynamicWhatsNewModal";
import DynamicProposalPreviewModal from "modules/trade-proposals/components/dynamicImports/DynamicProposalPreviewModal";
import DynamicSearchOverlayModal from "modules/common/components/navigation/DynamicSearchOverlay";
import { FeedbackSurveyTrigger } from "modules/common/components/FeedbackSurveyTrigger";
import DynamicAppReferralInviteModal from "modules/city/components/client/DynamicAppReferralInviteModal";

interface AppLayoutProps {
  children: ReactNode;
  header?: ReactNode;
}

/** Routes that should render without nav, footer, or drawers */
export const FOCUS_ROUTES = ["/x/onboarding"];
export const FRAMELESS_ROUTES = ["/r/"];

export function AppLayout({ children, header }: AppLayoutProps) {
  useNotificationToasts();
  const { user } = useUser();
  const pathname = usePathname();
  const canShowWhatsNew = user?.publicMetadata?.onboardingComplete === true;

  const isFocusRoute = FOCUS_ROUTES.some((route) =>
    pathname?.startsWith(route)
  );
  const isFramelessRoute = FRAMELESS_ROUTES.some((route) =>
    pathname?.startsWith(route)
  );

  // Use the hook with defaultValue false to avoid hydration mismatch
  const isDesktop = useMediaQuery("(min-width: 1280px)");

  // Ensure we're fully mounted before conditional rendering
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (isFramelessRoute) {
    return <main>{children}</main>;
  }

  // Focus mode: no nav, no footer, no drawers
  if (isFocusRoute) {
    return (
      <div className="bg-base-300 relative flex min-h-screen flex-col">
        <main className="flex flex-1 flex-col">{children}</main>
        <DynamicCardDetailsDialog />
        <DynamicQRCodeModal />
        <DynamicAppReferralInviteModal />
        <DynamicAddCardModal />
        <DynamicConfirmDeleteModal />
        <DynamicProposalPreviewModal />
      </div>
    );
  }

  return (
    <div className="bg-base-300 relative flex min-h-screen flex-col">
      {/* Only conditionally render based on media query after mounting to prevent hydration mismatch */}
      {isMounted ? (
        isDesktop ? (
          // Desktop layout
          <>
            {header}
            <main className="flex flex-1 flex-col">{children}</main>
            <Footer />
          </>
        ) : (
          // Mobile layout with navigation drawer
          <>
            <div className="drawer drawer-end flex-1">
              <input
                id="mobile-menu-drawer"
                type="checkbox"
                className="drawer-toggle"
              />

              {/* Mobile drawer content */}
              <div className="drawer-content relative flex flex-1 flex-col">
                {
                  header &&
                    // <div className="z-[45]">{
                    header
                  // }</div>
                }
                <main className="flex flex-1 flex-col">{children}</main>
                <Footer />
              </div>

              {/* Mobile navigation drawer side */}
              <div className="drawer-side z-[35]">
                <DrawerComponent />
              </div>
            </div>
          </>
        )
      ) : (
        // Pre-mount/SSR placeholder that won't cause hydration mismatch
        <>
          {header}
          <main className="flex-1">{children}</main>
          <Footer />
        </>
      )}

      {isMounted && <FeedbackSurveyTrigger variant="desktop" />}

      {/* Modals/Dialogs (shown on both) */}
      <DynamicCardDetailsDialog />
      <DynamicQRCodeModal />
      <DynamicAppReferralInviteModal />
      <DynamicAddCardModal />
      <DynamicConfirmDeleteModal />
      {canShowWhatsNew && <DynamicWhatsNewModal />}
      <DynamicProposalPreviewModal />
      <DynamicSearchOverlayModal />
    </div>
  );
}
